import React, { ReactNode, useState } from "react"
import styled from "styled-components"
import pull from "lodash.pull"

import { Box, Flex, RelativeBox } from "components/box"
import { ParagraphLarge, TextProps } from "components/text"
import BaseIcon from "components/icon"

const Content = styled(RelativeBox)`
  overflow: hidden;
  max-height: 0;

  .accordion--open & {
    max-height: 100%;
  }
`

const Header = styled(Flex)`
  position: relative;
  justify-content: space-between;
  align-items: baseline;

  :hover {
    cursor: pointer;
  }
`

const Container = styled(Box)`
  :not(:first-of-type) ${Header} {
    border-top: 1px solid ${props => props.theme.colors.evergreen};
  }
`

const Icon = styled(BaseIcon)`
  .accordion--closed && {
    transform: scaleY(-1);
  }
`

const ContentText = styled(ParagraphLarge).attrs({
  pb: [4, 4, 5, 5],
  pr: 5
})`
  &&& p {
    font-size: ${props => props.fontSize};
  }
`

export type Section = {
  header: ReactNode
  content: ReactNode
  isOpen?: boolean
}

export type AccordionProps = {
  sections: Section[]
  headerProps?: TextProps
  contentProps?: TextProps
}

const Accordion: React.FC<AccordionProps> = ({ sections, headerProps, contentProps }) => {
  const defaultOpenSectionsKeys = sections.filter(section => section.isOpen).map((_section, idx) => idx)
  const [openSections, setOpenSections] = useState(defaultOpenSectionsKeys)

  const isOpen = (key: number): boolean => (openSections ? openSections.includes(key) : false)

  const toggle = (key: number) => {
    const sectionsToUpdate = [...openSections]
    if (isOpen(key)) {
      pull(sectionsToUpdate, key)
    } else {
      sectionsToUpdate.push(key)
    }
    setOpenSections(sectionsToUpdate)
  }

  return (
    <>
      {sections.map(({ header, content }, idx) => (
        <Container key={idx} className={`accordion--${isOpen(idx) ? "open" : "closed"}`}>
          <Header onClick={() => toggle(idx)} py={[4, 4, 5, 5]}>
            <ParagraphLarge bold {...headerProps}>
              {header}
            </ParagraphLarge>
            <Icon name="caret" py={4} />
          </Header>
          <Content>
            <ContentText {...contentProps}>{content}</ContentText>
          </Content>
        </Container>
      ))}
    </>
  )
}

export default Accordion
